import {
  Avatar,
  Badge,
  BottomNavigationActionProps,
  Box,
  Chip,
  Skeleton,
  Stack,
  styled,
} from '@mui/material'
import { MouseEventHandler } from 'react'
import { Icons } from 'utoniq-ui'

import { useBrowserLink } from '@/components/lib/BrowserLink'
import { config } from '@/config'
import { useCurrentUser, useRouter, useTranslation } from '@/core'
import { usePrivateAction } from '@/lib/privateAction'
import { ArtistTabKeys } from '@/types/tabs'

export type TabList = {
  [key in ArtistTabKeys[number]]: {
    label: BottomNavigationActionProps['label']
    icon: BottomNavigationActionProps['icon']
    value: BottomNavigationActionProps['value']
    href?: string
    onClick?: MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>
  }
}

type UseTabListProps = {
  avatarUri?: string
  appLink?: string
  isLive?: boolean
  /** 配送申請・コンビニ支払いなどが必要な状態であればtrue */
  hasManagementTask?: boolean
  /** 未読の通知数 */
  unreadNotificationCount?: number
  totalUnreadMentionCount?: number
}

export const useTabList = ({
  avatarUri,
  appLink,
  isLive,
  unreadNotificationCount = 0,
  totalUnreadMentionCount = 0,
  hasManagementTask = false,
}: UseTabListProps): TabList => {
  const { t } = useTranslation()
  const { user: currentUser } = useCurrentUser()
  const { handleLoginModal } = usePrivateAction()
  const { routerPages, generatePath, router } = useRouter<{
    artistId: string
  }>()
  const artistId = router.query.artistId || config.artistId
  const { onClick } = useBrowserLink(appLink ?? '', {
    presentationStyle: 'popover',
  })

  /**
   * @todo
   * https://app.clickup.com/t/908127/UT-8948
   * お知らせ未読分のバッジ表示データ組み込み
   */
  const hasNotificationBadge = false

  return {
    home: {
      label: null,
      icon: !avatarUri ? (
        <Skeleton variant="circular" width={40} height={40} />
      ) : (
        <Stack sx={{ position: 'relative' }}>
          <Avatar
            src={avatarUri}
            sx={{
              border: theme =>
                isLive ? `1px solid ${theme?.palette?.like?.tap}` : 'none',
            }}
          />
          {isLive && <ChipStyled label="LIVE" />}
        </Stack>
      ),
      value: 'home',
      href: generatePath(routerPages.ArtistTop, { artistId }),
    },
    collections: {
      label: t('collection'),
      icon: <Icons.Collection color="inherit" />,
      value: 'collections',
      href: generatePath(routerPages.ArtistCollections, { artistId }),
    },
    myCollections: {
      label: t('collection'),
      icon: <Icons.Collection color="inherit" />,
      value: 'myCollections',
      href: generatePath(routerPages.ArtistMyCollections, { artistId }),
      onClick: handleLoginModal(),
    },
    gachas: {
      label: t('gacha'),
      icon: <Icons.Gacha color="inherit" />,
      value: 'gachas',
      href: generatePath(routerPages.ArtistGachaDetailLatest, {
        artistId,
      }),
    },
    missions: {
      label: t('mission'),
      icon: <Icons.Mission color="inherit" />,
      value: 'missions',
      href: generatePath(routerPages.ArtistMissions, { artistId }),
    },
    trade: {
      label: t('trade'),
      icon: <Icons.Market color="inherit" />,
      value: 'trade',
      href: generatePath(routerPages.ArtistTrade, { artistId }),
    },
    cart: {
      label: t('cart'),
      icon: <Icons.Store color="inherit" />,
      value: 'cart',
      href: routerPages.StoreCartList,
      // カートには匿名ログイン状態でもアクセスできる
      onClick: event => (currentUser ? () => {} : handleLoginModal()(event)),
    },
    notifications: {
      label: t('notification'),
      icon: (
        <Box sx={{ display: 'inline-flex', position: 'relative' }}>
          <StyledBadge
            badgeContent={
              hasNotificationBadge && unreadNotificationCount ? '' : undefined
            }
            color={'like' as any}
          />
          <Icons.Notice color="inherit" />
        </Box>
      ),
      value: 'notifications',
      href: routerPages.Notifications,
      onClick: handleLoginModal(),
    },
    myProfile: {
      label: t('myProfile'),
      icon: (
        <Box sx={{ display: 'inline-flex', position: 'relative' }}>
          <StyledBadge
            badgeContent={hasManagementTask ? '' : undefined}
            color={'like' as any}
          />
          <Icons.MyPage color="inherit" />
        </Box>
      ),
      value: 'myProfile',
      href: routerPages.MyProfile,
      onClick: handleLoginModal(),
    },
    posts: {
      label: t('post'),
      icon: <Icons.Post color="inherit" />,
      value: 'posts',
      href: generatePath(routerPages.ArtistPosts, { artistId }),
    },
    threads: {
      label: t('community'),
      icon: (
        <Box sx={{ display: 'inline-flex', position: 'relative' }}>
          <StyledBadge
            badgeContent={totalUnreadMentionCount}
            color={'like' as any}
            sx={{
              '& > span': {
                fontWeight: 'bold',
                fontSize: 9,
                minWidth: 20,
                height: 20,
              },
            }}
          />
          <Icons.Thread color="inherit" />
        </Box>
      ),
      value: 'threads',
      href: generatePath(routerPages.ArtistThreads, { artistId }),
    },
    store: {
      label: t('store'),
      icon: <Icons.Artist color="inherit" />,
      value: 'store',
      href: generatePath(routerPages.ArtistStore, { artistId }),
    },
    ownedItems: {
      label: t('ownedItems'),
      icon: <Icons.OwnedItems color="inherit" />,
      value: 'ownedItems',
      href: generatePath(routerPages.MyOwnedItems),
      onClick: handleLoginModal(),
    },
    events: {
      label: t('event'),
      icon: <Icons.ShokufesEvent color="inherit" />,
      value: 'events',
      href: generatePath(routerPages.ArtistEvents, { artistId }),
    },
    postVideos: {
      label: t('postVideos'),
      icon: (
        <Icons.AudioPlay
          color="inherit"
          sx={{ height: '20px', width: '20px', my: '2px' }}
        />
      ),
      value: 'postVideos',
      href: generatePath(routerPages.ArtistPostVideos, { artistId }),
    },
    gallery: {
      label: t('gallery'),
      icon: <Icons.Gallery color="inherit" />,
      value: 'gallery',
      href: generatePath(routerPages.ArtistGallery, { artistId }),
    },
    artistProfile: {
      label: t('artistProfile'),
      icon: <Icons.MyPage color="inherit" />,
      value: 'artistProfile',
      onClick: () => onClick(),
    },
  } as const
}

const ChipStyled = styled(Chip)(({ theme }) => ({
  color: 'white',
  backgroundColor: theme.palette.like.tap,
  position: 'absolute',
  bottom: 0,
  height: 'auto',
  width: 'auto',
  padding: 2,
  borderRadius: 2,
  left: '50%',
  transform: 'translateX(-50%)',
  '& .MuiChip-label': {
    paddingLeft: 4,
    paddingRight: 4,
  },
}))

const StyledBadge = styled(Badge)(() => ({
  position: 'absolute',
  color: 'white',
  top: 4,
  right: -2,
  width: 12,
  height: 12,
  '& > span': {
    fontWeight: 'bold',
    fontSize: 9,
    minWidth: 12,
    height: 12,
  },
}))
